/* eslint-disable react/require-default-props, react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { X, ExternalLink, Calendar, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../../../components/ui/button';

function EventDetailModal({ event, isOpen, onClose }) {
  if (!isOpen || !event) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDateRange = () => {
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);

    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    const startStr = start.toLocaleDateString('en-US', options);
    const endStr = end.toLocaleDateString('en-US', options);

    if (startStr === endStr) {
      return startStr;
    }
    return `${startStr} - ${endStr}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      role="button"
      tabIndex={0}
    >
      <div className="relative w-full max-w-lg mx-4 rounded-xl border bg-background shadow-xl animate-in fade-in-0 zoom-in-95 overflow-hidden">
        {/* Close button */}
        <button
          type="button"
          className="absolute right-4 top-4 z-10 rounded-full bg-background/80 p-1.5 opacity-70 hover:opacity-100 transition-opacity"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Event Image */}
        {event.image ? (
          <div className="relative h-48 bg-muted">
            <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="relative h-48 bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <ImageIcon className="h-16 w-16 text-muted-foreground/30" />
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Title */}
          <h2 className="text-xl font-semibold mb-3">{event.title}</h2>

          {/* Date */}
          <div className="flex items-center gap-2 text-muted-foreground mb-4">
            <Calendar className="h-4 w-4" />
            <span className="text-sm">{formatDateRange()}</span>
          </div>

          {/* Description if available */}
          {event.description && (
            <p className="text-sm text-muted-foreground mb-4">{event.description}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Close
            </Button>
            {event.link && (
              <Button className="flex-1 gap-2" asChild>
                <a href={event.link} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                  Learn More
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

EventDetailModal.propTypes = {
  event: PropTypes.shape({
    _id: PropTypes.string,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    image: PropTypes.string,
    link: PropTypes.string,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EventDetailModal;
